import { motion } from "framer-motion";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  FileText,
  Mail,
  Download,
  Users,
  Target,
  Heart,
  Shield,
  Newspaper,
  Building2,
  TrendingUp,
  Award,
  CheckCircle,
} from "lucide-react";

interface PressProps {
  onNavigate?: (page: string) => void;
}

export function Press({ onNavigate }: PressProps) {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const values = [
    {
      icon: Shield,
      title: "MHRA Compliant",
      description:
        "We strictly adhere to UK advertising regulations, ensuring no promotional content for prescription-only medicines reaches the public.",
    },
    {
      icon: Users,
      title: "Patient-First",
      description:
        "Every decision is made with verified patients in mind, providing them with unbiased information to make informed healthcare choices.",
    },
    {
      icon: Target,
      title: "Unbiased Information",
      description:
        "We randomise product and clinic listings for verified patients to prevent positioning bias and ensure fair representation.",
    },
    {
      icon: Heart,
      title: "Community-Driven",
      description:
        "Real patient experiences power our platform, creating a trusted community where patients help patients through authentic reviews.",
    },
  ];

  const keyFacts = [
    {
      label: "Platform Type",
      value: "Independent Review Platform",
    },
    {
      label: "Founded",
      value: "2024",
    },
    {
      label: "Headquarters",
      value: "United Kingdom",
    },
    {
      label: "Sector",
      value: "Medical Cannabis / Healthcare",
    },
    {
      label: "Mission",
      value: "Empower patients with unbiased information",
    },
    {
      label: "Compliance",
      value: "MHRA, GDPR, UK Regulations",
    },
  ];

  const resources = [
    {
      name: "Company Logo Pack",
      description: "High-resolution logos in various formats (PNG, SVG)",
      icon: FileText,
    },
    {
      name: "Brand Guidelines",
      description: "Visual identity, colour palette, and usage guidelines",
      icon: FileText,
    },
    {
      name: "Fact Sheet",
      description: "Key statistics, mission statement, and company information",
      icon: FileText,
    },
    {
      name: "Founder Bio",
      description: "Background and vision from the founding team",
      icon: FileText,
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl sm:text-5xl mb-4">Press Centre</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Learn about CannaBias, our mission to empower medical cannabis patients,
            and how we're building the UK's most trusted independent review platform.
          </p>
        </motion.div>

        {/* About CannaBias */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-12"
        >
          <Card className="p-8 glass-card">
            <h2 className="text-2xl mb-4">About CannaBias</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                CannaBias is the UK's first fully MHRA-compliant independent review
                platform for medical cannabis patients. We exist to solve a critical
                problem: patients prescribed medical cannabis have limited access to
                unbiased, peer-reviewed information about products and clinics.
              </p>
              <p>
                Unlike traditional review platforms, we operate under strict regulatory
                constraints. UK law prohibits the promotion of prescription-only medicines
                to the public, which means we cannot show product names or detailed
                information to non-verified users. Only verified patients—confirmed
                through secure clinic QR verification—can access full product listings,
                batch details, and submit reviews.
              </p>
              <p>
                This unique approach ensures compliance whilst empowering the patient
                community with the transparent, honest information they need to make
                informed healthcare decisions.
              </p>
            </div>
          </Card>
        </motion.div>

        {/* Our Values */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="mb-12"
        >
          <h2 className="text-2xl mb-6">Our Values</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <motion.div key={value.title} variants={item}>
                <Card className="p-6 glass-card h-full">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <value.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg mb-2">{value.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Why We Exist */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-12"
        >
          <Card className="p-8 glass-card">
            <h2 className="text-2xl mb-4">Why We Exist</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                The medical cannabis landscape in the UK is complex. Whilst legalised in
                2018, patients face significant challenges:
              </p>
              <div className="space-y-3 my-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-foreground">Information Gap:</strong> Limited
                    resources for comparing products and understanding real patient
                    experiences
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-foreground">Regulatory Constraints:</strong>{" "}
                    Strict advertising laws prevent clinics and pharmacies from promoting
                    products openly
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-foreground">Lack of Trust:</strong> No
                    independent, verified platform where patients can share honest reviews
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-foreground">Positioning Bias:</strong>{" "}
                    Commercial interests often influence product visibility and
                    recommendations
                  </p>
                </div>
              </div>
              <p>
                CannaBias bridges this gap. We provide a secure, compliant space where
                verified patients can access detailed product information, read authentic
                reviews from peers, and make better-informed decisions about their
                treatment.
              </p>
            </div>
          </Card>
        </motion.div>

        {/* Our Culture */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-12"
        >
          <Card className="p-8 glass-card">
            <h2 className="text-2xl mb-4">Our Culture & Approach</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">Transparency First:</strong> We
                openly communicate our compliance measures, verification processes, and
                anti-bias mechanisms. Every verified patient sees randomised product and
                clinic listings to ensure fairness.
              </p>
              <p>
                <strong className="text-foreground">Patient Privacy:</strong> We take
                GDPR compliance seriously. Patient data is protected, and reviews are
                moderated to remove personally identifiable information whilst maintaining
                authenticity.
              </p>
              <p>
                <strong className="text-foreground">Evidence-Based:</strong> Our platform
                is built on detailed patient feedback, including granular ratings for
                scent, taste, dryness, appearance, packaging, and effectiveness. This
                multi-dimensional approach provides richer insights than simple star
                ratings.
              </p>
              <p>
                <strong className="text-foreground">Non-Commercial:</strong> We do not
                sell products, accept payment for placement, or promote specific clinics.
                Our independence is fundamental to our mission.
              </p>
            </div>
          </Card>
        </motion.div>

        {/* Key Facts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-12"
        >
          <h2 className="text-2xl mb-6">Key Facts</h2>
          <Card className="p-8 glass-card">
            <div className="grid md:grid-cols-2 gap-6">
              {keyFacts.map((fact) => (
                <div key={fact.label} className="space-y-1">
                  <p className="text-sm text-muted-foreground">{fact.label}</p>
                  <p className="font-medium">{fact.value}</p>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>

        {/* Press Resources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mb-12"
        >
          <h2 className="text-2xl mb-6">Press Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {resources.map((resource, index) => (
              <Card
                key={resource.name}
                className="p-6 glass-card"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <resource.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium mb-1">
                      {resource.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {resource.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center mt-6">
            All press materials are available upon request. Please contact us at{" "}
            <a href="mailto:press@cannabias.co.uk" className="text-primary hover:underline">
              press@cannabias.co.uk
            </a>{" "}
            and we'll provide everything you need.
          </p>
        </motion.div>

        {/* Press Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <Card className="p-8 glass-card border-primary/20 bg-primary/5">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl mb-3">Press Enquiries</h2>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                We welcome enquiries from journalists, media outlets, event organisers,
                and news reporters. For press-related questions, interviews, or
                information requests, please contact:
              </p>
              <div className="inline-flex flex-col sm:flex-row gap-4 items-center justify-center">
                <Button
                  size="lg"
                  className="gradient-green hover:opacity-90 transition-opacity"
                  asChild
                >
                  <a href="mailto:press@cannabias.co.uk">
                    <Mail className="w-5 h-5 mr-2" />
                    press@cannabias.co.uk
                  </a>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-6">
                We typically respond to press enquiries within 24 hours
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}